import { useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo/useEventInfo';
import { addError } from '@shared/utils/logger';
import { sendMessageToPreview } from '@apps/admin/common/InlineEditManager/utils/SendMessageToPreview.utils';
import { useUpdateTravelSettingsMutation } from '@graphql/mutations/travelSettings.graphql.generated';
import { useDefaultNeighborhoodsNote } from '@shared/utils/stayAreas/useDefaultNeighborhoodsNote';
import type { PageNoteConfig } from '@shared/components/PageNoteDialog/PageNoteDialog.types';
import { useGetStartedTour } from '@shared/components/GetStartedOnboarding';

type UseStayAreasNoteSaveResult = Readonly<{
  dialogState: 'open' | 'closed';
  openDialog: () => void;
  closeDialog: () => void;
  handleSave: (pageNoteConfig: PageNoteConfig) => Promise<void>;
  isSaving: boolean;
}>;

/**
 * Persists the Neighborhoods (stay areas) section note on `travelSettings.accommodationsNeighborhoodsNote`
 * and pushes the new value to the guest preview, mirroring the Hotels-tab "Note to Guests" pattern.
 */
export const useStayAreasNoteSave = (): UseStayAreasNoteSaveResult => {
  const { completeTask: completeGetStartedTask } = useGetStartedTour();
  const { tq } = useTranslation('stayAreasAdmin');
  const { toast } = useToast();
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const defaultNote = useDefaultNeighborhoodsNote();

  const [dialogState, setDialogState] = useState<'open' | 'closed'>('closed');
  const [updateTravelSettings, { loading: isSaving }] = useUpdateTravelSettingsMutation();

  const openDialog = useEventCallback(() => setDialogState('open'));
  const closeDialog = useEventCallback(() => setDialogState('closed'));

  const handleSave = useEventCallback(async (pageNoteConfig: PageNoteConfig) => {
    if (!eventId) {
      toast(tq`Unable to save note. Please try again.`);
      return;
    }
    // The dialog prefills with the default copy so admin mirrors the guest fallback. If the host
    // saves it unchanged (or empty), persist '' so the guest keeps the dynamic default rather than
    // freezing the current default text into the event.
    const raw = pageNoteConfig.pageSubTitle ?? '';
    const note = raw.trim() === '' || raw === defaultNote ? '' : raw;
    try {
      await updateTravelSettings({
        variables: { payload: { eventId, accommodationsNeighborhoodsNote: note } },
        refetchQueries: ['AccommodationHotels'],
      });
      sendMessageToPreview([{ key: 'accommodationsNeighborhoodsNote', value: note }]);
      // "Get started" tour, step 4: writing the page note is Where to Stay work as much as adding a
      // hotel is. Only after the write succeeds — the catch below handles the failure.
      completeGetStartedTask('stay');
      setDialogState('closed');
    } catch (error) {
      addError(error, { context: 'useStayAreasNoteSave', eventId });
      toast(tq`Unable to save note. Please try again.`);
    }
  });

  return { dialogState, openDialog, closeDialog, handleSave, isSaving };
};
